import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { SportService, SportType } from 'app/services/sport/sport.service';
import { PrincipalService } from 'app/services/auth/principal.service';
import { PermissionService } from 'app/services/auth/permission.service';
import { sortByAttrAsc, sortByAttrDesc, sortByExtractedValue } from 'app/services/utils.service';
import { Role } from 'app/services/auth/role.constant';
import { Subscription } from 'rxjs';
import { Category, Event } from 'app/entities/event/event.types';
import { TextService } from 'app/services/text.service';

export type EventCardButtons = {
  results?: boolean;
  register?: boolean;
  registerJudges?: boolean;
  detailedInfo?: boolean;
};

type CssVars = Record<string, string>;

@Component({
  selector: 'event-card',
  templateUrl: 'event-card.html',
  standalone: false,
})
export class EventCardComponent implements OnInit, OnDestroy {
  @Input() event!: Event;
  @Input() buttons: EventCardButtons = {};
  @Input() showCategoriesOnMobile = false;

  registrationPhase2StartDate?: Date;

  categories: Category[] = [];
  categoriesWithResults: Category[] = [];
  categoriesWithInternalResults: Category[] = [];
  categoriesWithExternalResults: Category[] = [];

  externalResultSportTypes: SportType[] = [];
  categoriesCountBySportType: Record<string, number> = {};
  sportWithResultsCount = 0;
  imageContainerStyle: CssVars = {};

  showResults = false;
  showRegisterJudges = false;
  toDetailedInfo = false;
  showRegisterDefault = false;
  showRegisterWithOptions = false;

  private subscription?: Subscription;

  constructor(
    private readonly sportService: SportService,
    private readonly permissionService: PermissionService,
    private readonly principalService: PrincipalService,
    private readonly textService: TextService
  ) {}

  ngOnInit(): void {
    const e1 = this.event.registrationEndDate1;
    const e2 = this.event.registrationEndDate2;
    if (e1 && e2 && e1.getTime() !== e2.getTime()) {
      this.registrationPhase2StartDate = new Date(e1.getTime());
      this.registrationPhase2StartDate.setDate(this.registrationPhase2StartDate.getDate() + 1);
    }

    this.categories = this.event.categories.filter((c) => c.type === 'DEFAULT');
    this.categories.sort(sortByAttrAsc('sportType'));

    this.categoriesWithResults = this.sportService.categoriesWithResults(this.event.categories);
    this.categoriesWithInternalResults = sortByExtractedValue(
      this.sportService.categoriesWithInternalResults(this.event.categories),
      (c: Category) => c.sportType + ':' + c.name,
      true
    );
    this.categoriesWithExternalResults = sortByExtractedValue(
      this.sportService.categoriesWithExternalResults(this.event.categories),
      (c: Category) => c.sportType + ':' + c.name,
      true
    );

    const firstExternalResultBySport = new Map<SportType, Category>();
    for (const cat of this.categoriesWithExternalResults) {
      if (!firstExternalResultBySport.has(cat.sportType)) {
        firstExternalResultBySport.set(cat.sportType, cat);
      }
    }
    this.externalResultSportTypes = Array.from(firstExternalResultBySport.keys()).sort();

    this.sportWithResultsCount = this.categoriesWithResults.reduce<SportType[]>((acc, c) => {
      if (!acc.includes(c.sportType as SportType)) {
        acc.push(c.sportType as SportType);
      }
      return acc;
    }, []).length;

    this.categoriesCountBySportType = this.categories.reduce<Record<string, number>>((acc, c) => {
      acc[c.sportType] = (acc[c.sportType] ?? 0) + 1;
      return acc;
    }, {});

    this.showResults =
      this.buttons.results !== undefined
        ? !!this.buttons.results
        : this.eventStarted() && this.categoriesWithResults.length > 0;

    this.showRegisterJudges = this.buttons.registerJudges ?? false;
    this.toDetailedInfo = !!this.buttons.detailedInfo;

    this.imageContainerStyle = { 'background-image': `url('${this.selectBgImageUrl()}')` };

    this.subscription = this.principalService.getCurrentIdentity().subscribe({
      next: () => this.defineButtonsVisibility(),
    });
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  openExtSportResults(sportType: SportType): void {
    const year = this.event.startDate.getFullYear();
    const isTrampoline = sportType === SportType.TUM;
    const url = isTrampoline
      ? `https://sporttech.io/explore?year=${year}&country=FIN`
      : `https://live.sporteventsystems.se/score/?country=fin&year=${year}`;
    window.open(url, '_blank');
  }

  openExtResultsForDefaultSport(): void {
    const sportType = this.externalResultSportTypes[0];
    if (sportType) {
      this.openExtSportResults(sportType);
    }
  }

  public hasSecondPhaseRegistration(): boolean {
    if (this.event.registrationEndDate1 && this.event.registrationEndDate2) {
      return this.event.registrationEndDate1.getTime() !== this.event.registrationEndDate2.getTime();
    }
    return false;
  }

  private defineButtonsVisibility(): void {
    const showRegister =
      this.buttons.register ??
      (((this.registrationActive() || this.permissionService.isEventOrganizerUser(this.event)) &&
        this.hasRegistrationRights()) ||
        this.permissionService.isFedUser() ||
        (this.registrationStarted() && this.permissionService.isEventAdmin(this.event)));

    const externalRegistrationsEnabled = this.event.settings?.externalRegistrationsEnabled;

    if (externalRegistrationsEnabled) {
      this.showRegisterDefault = showRegister && this.principalService.isAuthenticated();
      this.showRegisterWithOptions = showRegister && !this.principalService.isAuthenticated();
    } else {
      this.showRegisterDefault = showRegister;
      this.showRegisterWithOptions = false;
    }

    this.showRegisterJudges =
      this.buttons.register !== undefined ? !!this.buttons.registerJudges : this.hasJudgeRegistrationRights();
  }

  private eventStarted(): boolean {
    return this.event.startDate < new Date();
  }

  private registrationActive(): boolean {
    if (!this.event.registrationStartDate || !this.event.registrationEndDate1 || !this.event.registrationEndDate2) {
      return false;
    }
    const now = new Date();
    const finalRegEndDate = new Date(this.event.registrationEndDate2.getTime());
    finalRegEndDate.setDate(finalRegEndDate.getDate() + 1);
    return now > this.event.registrationStartDate && now < finalRegEndDate;
  }

  private registrationStarted(): boolean {
    if (!this.event.registrationStartDate || !this.event.registrationEndDate1 || !this.event.registrationEndDate2) {
      return false;
    }
    return new Date() > this.event.registrationStartDate;
  }

  private hasJudgeRegistrationRights(): boolean {
    if (!this.principalService.isAuthenticated()) {
      return false;
    }
    return (
      this.principalService.hasAnyAuthority(Role.CLUB_USER_ROLES) ||
      this.principalService.hasAnyAuthority(Role.FED_ROLES)
    );
  }

  private hasRegistrationRights(): boolean {
    if (!this.principalService.isAuthenticated()) {
      return true;
    }
    return this.principalService.hasAnyAuthority(Role.CLUB_USER_ROLES);
  }

  private selectBgImageUrl(): string {
    if (this.event.categories.length > 0) {
      const categoryCountBySport = this.event.categories.reduce(
        (acc, category) => {
          const st = category.sportType as SportType;
          acc[st] = (acc[st] ?? 0) + 1;
          return acc;
        },
        {} as Record<SportType, number>
      );

      const sportTypeAndCategoryCount = Object.entries(categoryCountBySport).map(([sportType, categoryCount]) => ({
        sportType: sportType as SportType,
        categoryCount,
      }));
      sportTypeAndCategoryCount.sort(sortByAttrDesc('categoryCount'));

      let mainSportType = sportTypeAndCategoryCount[0]?.sportType;
      if (mainSportType === SportType.STARA && sportTypeAndCategoryCount.length > 1) {
        mainSportType = sportTypeAndCategoryCount[1].sportType;
      }

      switch (mainSportType) {
        case SportType.RHYTHMIC_GYMNASTICS:
          return 'https://kisanet-static-content.fra1.cdn.digitaloceanspaces.com/event-logo/rg-event-logo-1.jpg';
        case SportType.AGG:
          return 'https://kisanet-static-content.fra1.cdn.digitaloceanspaces.com/event-logo/agg-event-logo-1.jpg';
        case SportType.WAG:
          return 'https://kisanet-static-content.fra1.cdn.digitaloceanspaces.com/event-logo/ntv-event-logo-1.jpg';
        case SportType.MAG:
          return 'https://kisanet-static-content.fra1.cdn.digitaloceanspaces.com/event-logo/mtv-event-logo-2.jpg';
        case SportType.TUM:
          return 'https://kisanet-static-content.fra1.cdn.digitaloceanspaces.com/event-logo/tum-event-logo-1.jpg';
        case SportType.AKRO:
          return 'https://kisanet-static-content.fra1.cdn.digitaloceanspaces.com/event-logo/akro-event-logo-1.jpg';
        case SportType.TANVO:
          return 'https://kisanet-static-content.fra1.cdn.digitaloceanspaces.com/event-logo/tanvo-event-logo-1.jpg';
        case SportType.LUMO:
          return 'https://kisanet-static-content.fra1.cdn.digitaloceanspaces.com/event-logo/lumo-event-logo-1.jpg';
        case SportType.TG:
          return 'https://kisanet-static-content.fra1.cdn.digitaloceanspaces.com/event-logo/tg-event-logo-1.jpg';
        case SportType.KA:
          return 'https://kisanet-static-content.fra1.cdn.digitaloceanspaces.com/event-logo/ka-event-logo-1.jpg';
        case SportType.STARA:
          return 'https://kisanet-static-content.fra1.cdn.digitaloceanspaces.com/event-logo/stara-event-logo-2.jpg';
        case SportType.GFL:
          return 'https://kisanet-static-content.fra1.cdn.digitaloceanspaces.com/event-logo/gfl-event-logo-1.jpg';
        case SportType.DANCE_LUMO:
          return 'https://kisanet-static-content.fra1.cdn.digitaloceanspaces.com/event-logo/dance-lumo-event-logo-1.jpg';
        case SportType.TUL_DANCE:
          return 'https://kisanet-static-content.fra1.cdn.digitaloceanspaces.com/event-logo/tul-dance-event-logo-1.jpg';
        case SportType.MAGIC_MOVE:
          return 'https://kisanet-static-content.fra1.cdn.digitaloceanspaces.com/event-logo/magic-move-event-logo-230x230-2.jpg';
        case SportType.GYM_SKILLS:
          return 'https://kisanet-static-content.fra1.cdn.digitaloceanspaces.com/event-logo/gym-skills-logo.jpg';
        default:
          return 'images/event-logo/event.jpg';
      }
    }
    return 'images/event-logo/event.jpg';
  }
}
